import { useCallback, useState } from 'react';
import { Button, SelectMenu } from '@scale/scaleui';
import _ from 'lodash';
import { twMerge } from 'tailwind-merge';
import { toast } from 'react-toastify';
import { PerTurnFeedback, PerTurnAuditGrade } from '@scale/shared/defaults';
import produce from 'immer';
import { TurnFeedback } from './TurnFeedback';
import { AddTurnFeedbackModal } from './AddTurnFeedbackModal';

type Props = {
  projectId: string;
  disableFeedback: boolean;
  classes: any;
  updatePerTurnFeedback: (perTurnFeedback: PerTurnFeedback) => void;
  perTurnFeedback: PerTurnFeedback;
};

function getEmptyTurnFeedback(turnIndex: number): PerTurnAuditGrade {
  return {
    errorCategories: [],
    feedback: '',
    turnIndex,
  };
}

export const FeedbackPerTurn = ({
  projectId,
  disableFeedback,
  classes,
  perTurnFeedback,
  updatePerTurnFeedback,
}: Props) => {
  const [turnIndex, setTurnIndex] = useState<number>(0);

  const [modalOpen, setModalOpen] = useState(false);

  const updateTurnFeedbackField = useCallback(
    (fieldValue: any, fieldName: string, index: number) => {
      updatePerTurnFeedback(
        produce(perTurnFeedback, (draft: { [x: string]: any }[]) => {
          if (!draft[index]) {
            draft[index] = getEmptyTurnFeedback(index);
          }
          draft[index][fieldName] = fieldValue;
          return draft;
        }),
      );
    },
    [perTurnFeedback, updatePerTurnFeedback],
  );

  return (
    <div className="w-full">
      {modalOpen && (
        <AddTurnFeedbackModal
          onClose={() => setModalOpen(false)}
          onAddTurn={(index: string) => {
            setModalOpen(false);
            const splitIndex = index.split('-');
            const intIndex = parseInt(splitIndex[0]) - 1;

            try {
              if (splitIndex.length > 1) {
                // We need to subtract 1 from the start index because the user see turns using 1-based indexing
                const start = parseInt(splitIndex[0]) - 1;
                const end = parseInt(splitIndex[1]) - 1;
                const temp: PerTurnFeedback = { ...perTurnFeedback };
                for (let i = start; i <= end; i++) {
                  // If the turn feedback already exists we don't
                  if (perTurnFeedback[i]) {
                    continue;
                  }
                  temp[i] = getEmptyTurnFeedback(i);
                }
                updatePerTurnFeedback(temp);
              } else {
                updatePerTurnFeedback({
                  ...perTurnFeedback,
                  [intIndex]: getEmptyTurnFeedback(intIndex),
                });
              }
            } catch (e) {
              toast.error('Invalid turn index');
              return;
            }
            if (perTurnFeedback[intIndex]) {
              setTurnIndex(intIndex);
            }
          }}
        />
      )}
      <div className={twMerge(classes.containerSection, 'flex flex-row items-center gap-2')}>
        <SelectMenu
          className="w-1/2"
          isSelectionTruncated
          menuDirection="bottom"
          disabled={
            disableFeedback || !perTurnFeedback || Object.keys(perTurnFeedback).length === 0
          }
          options={
            Object.keys(perTurnFeedback ?? {}).map((key: string) => ({
              id: key,
              name: `Turn ${parseInt(key) + 1}`,
            })) ?? []
          }
          selectedOptionId={turnIndex.toString()}
          setSelected={(optionId: any) => {
            setTurnIndex(parseInt(optionId));
          }}
          variant="default"
        />
        <Button
          className="leading-3 h-full"
          disabled={disableFeedback}
          onClick={() => setModalOpen(true)}
        >
          Add per turn Ratings and Feedback
        </Button>
      </div>
      {_.isNil(turnIndex) || Object.keys(perTurnFeedback ?? {}).length === 0 ? null : (
        <TurnFeedback
          classes={classes}
          turnIndex={turnIndex}
          turnFeedback={perTurnFeedback[turnIndex]}
          updateTurnFeedbackField={updateTurnFeedbackField}
          projectId={projectId}
          disableFeedback={disableFeedback}
        />
      )}
    </div>
  );
};
